// models/Del/DelSdsReference.js

// An SDS attached to a DEL from the 3E catalog (Misc > SDS). Rows are immutable snapshots: add/remove only.

import { v4 as uuidv4 } from "uuid";

// Dates arrive as "YYYY-MM-DD" or an ISO datetime string from a broadcast; keep the calendar day only
const toDateString = (value) =>
  typeof value === "string" && value ? value.slice(0, 10) : null;

const toNullableString = (value) =>
  value === null || value === undefined || value === "" ? null : String(value);

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const BARE_HEX_RE = /^[0-9a-f]{32}$/;

// Canonical lowercase hyphenated GUID (braces, urn:uuid: and bare hex accepted), matching the server's
// str(uuid.UUID(v)); anything that is not a GUID is passed through so it can still be rejected upstream
export const normalizeProductUid = (value) => {
  const raw = toNullableString(value);
  if (!raw) return null;
  const bare = raw
    .trim()
    .replace(/^urn:uuid:/i, "")
    .replace(/^\{(.*)\}$/, "$1")
    .toLowerCase();
  if (GUID_RE.test(bare)) return bare;
  if (BARE_HEX_RE.test(bare)) {
    return `${bare.slice(0, 8)}-${bare.slice(8, 12)}-${bare.slice(12, 16)}-${bare.slice(16, 20)}-${bare.slice(20)}`;
  }
  return raw;
};

export class DelSdsReference {
  constructor(data = {}) {
    this.rowId = data.id || uuidv4();
    this.productUid = normalizeProductUid(data.product_uid);
    this.productName = data.product_name || "";
    this.manufacturerName = data.manufacturer_name || "";
    this.manufacturerPartNumber = data.manufacturer_part_number || "";
    this.documentLink = toNullableString(data.document_link);
    this.revisionDate = toDateString(data.revision_date);
    this.country = toNullableString(data.country);
    this.language = toNullableString(data.language);
    this.addedBy = toNullableString(data.added_by);
  }

  // Build a reference from a /api/sds/search hit (snake_case) for the given employee
  static fromSearchHit(hit = {}, addedBy = null) {
    return new DelSdsReference({
      product_uid: hit.product_uid,
      product_name: hit.product_name,
      manufacturer_name: hit.manufacturer_name,
      manufacturer_part_number: hit.manufacturer_part_number,
      document_link: hit.document_link,
      revision_date: hit.revision_date,
      country: hit.country,
      language: hit.language,
      added_by: addedBy,
    });
  }

  // Snake_case snapshot (matches the pull shape and the buildSdsDownloadUrl input)
  toJson() {
    return {
      id: this.rowId,
      product_uid: this.productUid,
      product_name: this.productName,
      manufacturer_name: this.manufacturerName,
      manufacturer_part_number: this.manufacturerPartNumber,
      document_link: this.documentLink,
      revision_date: this.revisionDate,
      country: this.country,
      language: this.language,
      added_by: this.addedBy,
    };
  }

  // "add" carries the snapshot, "remove" only the id; del_id is injected by delSocketService
  toPatch(action) {
    const patch = {
      patch_type: "sds_reference",
      action: action,
      id: this.rowId,
    };
    if (action === "remove") return patch;
    return { ...this.toJson(), ...patch };
  }
}
